<template>
  <div
    class="upload-zone-wrap"
    :class="{
      'is-uploaded': uploadObj.done,
      'is-loading': uploadObj.loading,
      'has-file': uploadObj.file && !uploadObj.done && !uploadObj.loading
    }"
  >
    <!-- Label Row -->
    <div class="uz-label-row">
      <v-icon small color="#1E4276" class="uz-doc-icon">mdi-file-document-outline</v-icon>
      <span class="uz-label-text">{{ label }}<span v-if="required" class="uz-required">*</span></span>
      <span class="uz-hint">Max 6MB · PDF, JPEG, PNG</span>
    </div>

    <!-- ── UPLOADING STATE ── -->
    <div v-if="uploadObj.loading" class="uz-uploading-row">
      <div class="uz-uploading-left">
        <div class="uz-file-icon-wrap uz-file-icon-loading">
          <v-progress-circular indeterminate size="18" width="2.5" color="#00AEEF"></v-progress-circular>
        </div>
        <div class="uz-uploading-info">
          <span class="uz-uploading-name">{{ uploadObj.file ? uploadObj.file.name : 'Uploading…' }}</span>
          <span class="uz-uploading-sub">Uploading, please wait…</span>
        </div>
      </div>
      <!-- Animated shimmer progress bar -->
      <div class="uz-progress-bar-wrap">
        <div class="uz-progress-bar-track">
          <div class="uz-progress-bar-fill"></div>
        </div>
      </div>
    </div>

    <!-- ── UPLOADED / DONE STATE ── -->
    <div v-else-if="uploadObj.done && uploadObj.file" class="uz-success-row">
      <div class="uz-success-icon-wrap">
        <v-icon size="16" color="#10B981">mdi-check-circle</v-icon>
      </div>
      <div class="uz-success-info">
        <span class="uz-success-name">{{ uploadObj.file.name }}</span>
        <span class="uz-success-sub">Uploaded successfully</span>
      </div>
      <button class="uz-remove-btn" @click.prevent="$emit('remove')" title="Remove file">
        <v-icon x-small color="#EF4444">mdi-close-circle</v-icon>
      </button>
    </div>

    <!-- ── DROP ZONE (no file yet) ── -->
    <label v-else class="uz-drop-zone">
      <input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        class="uz-hidden-input"
        @change="e => $emit('file-selected', e.target.files[0])"
      />
      <div class="uz-icon-bubble">
        <v-icon size="20" color="#00AEEF">mdi-cloud-upload-outline</v-icon>
      </div>
      <span class="uz-drop-text">Click to upload or drag &amp; drop</span>
    </label>
  </div>
</template>

<script>
export default {
  name: 'UploadZone',
  props: {
    label: String,
    required: Boolean,
    uploadObj: Object,
  },
  emits: ['file-selected', 'remove'],
};
</script>

<style scoped>
/* ── Wrapper ── */
.upload-zone-wrap {
  background: #ffffff;
  border: 1.5px solid #E8EDF2;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
}

.upload-zone-wrap.is-uploaded {
  border-color: #10B981;
  background: rgba(16, 185, 129, 0.03);
  box-shadow: 0 0 0 1px #10B981;
}

.upload-zone-wrap.is-loading {
  border-color: #00AEEF;
  background: rgba(0, 174, 239, 0.03);
  box-shadow: 0 0 0 1px rgba(0, 174, 239, 0.35);
}

/* ── Label Row ── */
.uz-label-row {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
}

.uz-label-text {
  font-size: 0.82rem;
  font-weight: 700;
  color: #1E4276;
  flex: 1;
  min-width: 0;
}

.uz-required {
  color: #EF4444;
  margin-left: 2px;
}

.uz-hint {
  font-size: 0.68rem;
  color: #94A3B8;
  font-weight: 500;
  white-space: nowrap;
}

/* ── Drop Zone ── */
.uz-drop-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 1.5px dashed #CBD5E1;
  border-radius: 8px;
  padding: 18px 12px;
  cursor: pointer;
  background: #F8FBFF;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  position: relative;
  min-height: 80px;
}

.uz-drop-zone:hover {
  border-color: #00AEEF;
  background: rgba(0, 174, 239, 0.04);
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.08);
}

.uz-hidden-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 10;
}

.uz-icon-bubble {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(0, 174, 239, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  pointer-events: none;
}

.uz-drop-text {
  font-size: 0.75rem;
  color: #64748B;
  font-weight: 500;
  text-align: center;
  pointer-events: none;
}

/* ── UPLOADING ROW ── */
.uz-uploading-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: rgba(0, 174, 239, 0.05);
  border: 1px solid rgba(0, 174, 239, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  animation: uz-fade-in 0.2s ease-out;
}

.uz-uploading-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.uz-file-icon-wrap {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.uz-file-icon-loading {
  background: rgba(0, 174, 239, 0.1);
}

.uz-uploading-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.uz-uploading-name {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1E4276;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.uz-uploading-sub {
  font-size: 0.68rem;
  color: #00AEEF;
  font-weight: 600;
}

/* ── Animated shimmer progress bar ── */
.uz-progress-bar-wrap {
  width: 100%;
}

.uz-progress-bar-track {
  width: 100%;
  height: 4px;
  background: rgba(0, 174, 239, 0.15);
  border-radius: 99px;
  overflow: hidden;
}

.uz-progress-bar-fill {
  height: 100%;
  width: 45%;
  background: linear-gradient(90deg, #00AEEF, #1E4276, #00AEEF);
  background-size: 200% 100%;
  border-radius: 99px;
  animation: uz-shimmer 1.4s ease-in-out infinite;
}

@keyframes uz-shimmer {
  0%   { transform: translateX(-120%); }
  100% { transform: translateX(320%); }
}

/* ── SUCCESS ROW ── */
.uz-success-row {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(16, 185, 129, 0.07);
  border: 1px solid rgba(16, 185, 129, 0.25);
  border-radius: 7px;
  padding: 9px 12px;
  animation: uz-fade-in 0.2s ease-out;
}

.uz-success-icon-wrap {
  flex-shrink: 0;
}

.uz-success-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
}

.uz-success-name {
  font-size: 0.78rem;
  font-weight: 600;
  color: #1E4276;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.uz-success-sub {
  font-size: 0.68rem;
  color: #10B981;
  font-weight: 600;
}

.uz-remove-btn {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  transition: transform 0.15s;
}

.uz-remove-btn:hover {
  transform: scale(1.2);
}

/* ── Shared animation ── */
@keyframes uz-fade-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>
